import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db, auth } from '../firebase';

export default function Home({ openContactModal, setOpenContactModal }) {
  const [properties, setProperties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(true);

  // Professional Indian Currency Formatter (Crores & Lakhs)
  const formatIndianPrice = (amount) => {
    if (!amount && amount !== 0) return '';
    const num = Number(amount);
    
    if (num >= 10000000) {
      const cr = num / 10000000;
      return `${cr % 1 === 0 ? cr.toFixed(0) : cr.toFixed(1)} Cr.`;
    } else if (num >= 100000) {
      const lakh = num / 100000;
      return `${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(1)} Lakh`;
    } else {
      return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "properties"));
        const propertyList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProperties(propertyList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching properties: ", error);
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  // Filter properties based on search and category
  let filteredProperties = properties.filter(property => {
    const matchesSearch = 
      property.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = selectedType === 'All' || property.type === selectedType;

    return matchesSearch && matchesType;
  });

  // Sorting logic (Price & Newest)
  if (sortBy === 'low-high') {
    filteredProperties.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
  } else if (sortBy === 'high-low') {
    filteredProperties.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
  }

  const categories = ['All', '1 BHK Flat', '2 BHK Flat', '3 BHK Flat', 'Luxury Villa', 'Penthouse'];

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Hero Section */}
      <div className="relative bg-slate-900 text-white py-28 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')` }}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/80 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-widest">
            Advanced Real Estate Portal
          </span>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mt-4 mb-6 leading-tight">
            Discover Your <span className="text-indigo-400">Dream Sanctuary</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mb-8">
            Explore verified luxury flats, modern penthouses, and independent villas with smart filters and instant search.
          </p>

          {/* Search Box */}
          <div className="bg-white p-3 rounded-2xl shadow-2xl max-w-3xl flex flex-col md:flex-row gap-3">
            <input 
              type="text" 
              placeholder="Search by city, landmark or property title..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-grow px-4 py-3 text-slate-800 rounded-xl focus:outline-none bg-slate-50 border border-slate-200 text-sm"
            />
            <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl font-medium transition shadow-md text-sm">
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Category Filter Tabs & Sorting Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="bg-white p-4 rounded-2xl shadow-xl flex flex-col md:flex-row justify-between items-center gap-4 border border-slate-100">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedType(category)}
                className={`px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition ${
                  selectedType === category
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                    : 'bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Sorting Dropdown */}
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Sort By:</span>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-700 text-xs md:text-sm rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="default">Default</option>
              <option value="low-high">Price: Low to High</option>
              <option value="high-low">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Property Grid Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              {selectedType === 'All' ? 'All Available Properties' : selectedType}
            </h2>
            <p className="text-slate-500 text-sm mt-1">Showing {filteredProperties.length} live results</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-500 font-medium">Loading properties...</div>
        ) : filteredProperties.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <h3 className="text-xl font-bold text-slate-800">No properties found</h3>
            <p className="text-slate-500 text-sm mt-1">Try switching categories or adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredProperties.map(property => (
              <div key={property.id} className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition duration-300 overflow-hidden border border-slate-100 flex flex-col justify-between">
                <div>
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={property.image || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2"} 
                      alt={property.title} 
                      className="w-full h-full object-cover hover:scale-105 transition duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-slate-900/80 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-lg">
                      {property.type || "Flat"}
                    </span>

                    {/* Wishlist Heart Button */}
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        if (!auth.currentUser) {
                          alert("Please login first to save properties to your wishlist!");
                          return;
                        }
                        const uid = auth.currentUser.uid;
                        let saved = JSON.parse(localStorage.getItem(`saved_${uid}`)) || [];
                        if (saved.includes(property.id)) {
                          saved = saved.filter(id => id !== property.id);
                          alert("Removed from wishlist");
                        } else {
                          saved.push(property.id);
                          alert("Saved to wishlist successfully!");
                        }
                        localStorage.setItem(`saved_${uid}`, JSON.stringify(saved));
                      }}
                      className="absolute top-4 right-4 bg-white/80 hover:bg-white p-2 rounded-full shadow-md backdrop-blur-md transition text-rose-500"
                      title="Save to Wishlist"
                    >
                      ❤️
                    </button>
                  </div>
                  <div className="p-6">
                    <p className="text-xs text-indigo-600 font-semibold tracking-wide uppercase">{property.location}</p>
                    <h3 className="text-lg font-bold text-slate-900 mt-1 line-clamp-1">{property.title}</h3>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-0 flex justify-between items-center border-t border-slate-50 mt-4">
                  <div>
                    <span className="text-xs text-slate-400 block">Price</span>
                    <span className="text-xl font-extrabold text-indigo-600">
                      ₹ {formatIndianPrice(property.price)}
                    </span>
                  </div>
                  <Link 
                    to={`/property/${property.id}`} 
                    className="bg-slate-900 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-600 transition shadow-sm"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ Section */}
      <div className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="bg-indigo-500/20 text-indigo-400 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
              Got Questions?
            </span>
            <h2 className="text-3xl font-bold mt-3">Frequently Asked Questions</h2>
            <p className="text-slate-400 text-sm mt-2">Everything you need to know about buying, selling, and listing on PrimiumFlats.</p>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">How can I post a property on PrimiumFlats?</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                First, sign in to your account using the Authentication page. Once logged in, click on the "Post Property" option in the navigation bar, fill out the property details, and click publish to make it live instantly.
              </p>
            </div>

            <div className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Are there any brokerage or hidden charges?</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                No! PrimiumFlats acts as a direct peer-to-peer bridge between buyers and verified property owners. We do not charge any brokerage fees.
              </p>
            </div>

            <div className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">How do I contact a property owner?</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                On any property card, click "View Details" to open the full property page to find direct action buttons to call or chat with the owner.
              </p>
            </div>

            <div className="bg-slate-800/60 border border-slate-700/60 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-2">Can I save properties to view later?</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Yes! Logged-in users can click the heart (❤️) icon on any property card to save it to their personal Wishlist.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}